/**
 * useQuotaWarnings - Manage progressive quota warnings
 *
 * Features:
 * - Track 50%, 80%, 90% usage thresholds
 * - Persist warning state to AsyncStorage
 * - Reset warnings when quota period resets
 * - Analytics event tracking
 * - Supports both anonymous and authenticated users
 */
import { useState, useEffect, useCallback } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from './useEntitlements';
import { captureEvent } from '../lib/analytics';

const STORAGE_KEY = '@DramaDetector/quotaWarningsShown';

export type WarningLevel = '50_percent' | '80_percent' | '90_percent';
export type WarningType = 'banner' | 'toast' | 'modal';

interface WarningState {
  userId: string;
  tier: 'free' | 'drama_plus' | 'dramax' | 'anonymous';
  warnings: {
    '50_percent': { shown: boolean; timestamp: string };
    '80_percent': { shown: boolean; timestamp: string };
    '90_percent': { shown: boolean; timestamp: string };
  };
  lastResetDate: string;
}

interface UseQuotaWarningsResult {
  shouldShowWarning: (level: WarningLevel) => boolean;
  markWarningShown: (level: WarningLevel, type: WarningType) => Promise<void>;
  resetWarnings: () => Promise<void>;
  warningState: WarningState | null;
}

/**
 * Create default warning state
 */
function createDefaultState(userId: string, tier: string): WarningState {
  return {
    userId,
    tier: tier as WarningState['tier'],
    warnings: {
      '50_percent': { shown: false, timestamp: '' },
      '80_percent': { shown: false, timestamp: '' },
      '90_percent': { shown: false, timestamp: '' },
    },
    lastResetDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD
  };
}

/**
 * Load warning state from AsyncStorage
 */
async function loadWarningState(userId: string, tier: string): Promise<WarningState> {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return createDefaultState(userId, tier);
    }

    const state: WarningState = JSON.parse(stored);

    // Check if we need to reset warnings (new day)
    const today = new Date().toISOString().split('T')[0];
    if (state.lastResetDate !== today) {
      return createDefaultState(userId, tier);
    }

    // Check if user changed (logged in/out)
    if (state.userId !== userId) {
      return createDefaultState(userId, tier);
    }

    return state;
  } catch (error) {
    console.error('Failed to load warning state:', error);
    return createDefaultState(userId, tier);
  }
}

/**
 * Save warning state to AsyncStorage
 */
async function saveWarningState(state: WarningState): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save warning state:', error);
  }
}

/**
 * Hook to manage quota warnings
 */
export function useQuotaWarnings(): UseQuotaWarningsResult {
  const { user } = useAuth();
  const { tier: entitlementTier } = useEntitlements();
  const [warningState, setWarningState] = useState<WarningState | null>(null);

  // Determine user ID and tier from entitlements
  const userId = user?.id || 'anonymous';
  const tier = entitlementTier;

  // Load warning state on mount
  useEffect(() => {
    loadWarningState(userId, tier).then(setWarningState);
  }, [userId, tier]);

  /**
   * Check if a warning should be shown
   */
  const shouldShowWarning = useCallback(
    (level: WarningLevel): boolean => {
      if (!warningState) return false;

      // Check if warning was already shown
      return !warningState.warnings[level].shown;
    },
    [warningState]
  );

  /**
   * Mark a warning as shown and track analytics
   */
  const markWarningShown = useCallback(
    async (level: WarningLevel, type: WarningType) => {
      if (!warningState) return;

      // Update state
      const newState: WarningState = {
        ...warningState,
        warnings: {
          ...warningState.warnings,
          [level]: {
            shown: true,
            timestamp: new Date().toISOString(),
          },
        },
      };

      setWarningState(newState);
      await saveWarningState(newState);

      // Track analytics event
      captureEvent('Usage_WarningShown', {
        user_id: userId,
        user_tier: tier,
        warning_level: level,
        limit_type: 'daily_analyses',
        warning_type: type,
      } as any);
    },
    [warningState, userId, tier]
  );

  /**
   * Reset all warnings (called when quota resets)
   */
  const resetWarnings = useCallback(async () => {
    const newState = createDefaultState(userId, tier);
    setWarningState(newState);
    await saveWarningState(newState);
  }, [userId, tier]);

  return {
    shouldShowWarning,
    markWarningShown,
    resetWarnings,
    warningState,
  };
}

/**
 * Determine which warning to show based on usage percentage
 * Returns null if no warning should be shown
 */
export function determineWarningLevel(
  usagePercent: number,
  shouldShowWarning: (level: WarningLevel) => boolean
): { level: WarningLevel; type: WarningType } | null {
  // Check in priority order (90% > 80% > 50%)
  if (usagePercent >= 90 && shouldShowWarning('90_percent')) {
    return { level: '90_percent', type: 'modal' };
  }

  if (usagePercent >= 80 && shouldShowWarning('80_percent')) {
    return { level: '80_percent', type: 'toast' };
  }

  if (usagePercent >= 50 && shouldShowWarning('50_percent')) {
    return { level: '50_percent', type: 'banner' };
  }

  return null;
}
